import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { CrDataWarning } from '@chancenwerk/client/components/chancen-component-data-warning.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { DurchfuehrungsTag } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { CourseChancenschule } from '@chancenwerk/shared/courses/CourseChancenschule.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { ChancenRoleAspect } from '@chancenwerk/shared/semantics/ChancenRoleAspect.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import { StudentType, type Weekday, weekdayIndex } from '@chancenwerk/enums/enums.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import type { ChromiumFlex } from '@chancenwerk/client/design/components/cr-flex.js';
import '@chancenwerk/client/design/components/cr-grid.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import { KPFAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/KPFAspect.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { StudentTypeAspect } from '@chancenwerk/shared/master-data-management/users/entities/aspects/StudentTypeAspect.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { map, switchMap, takeUntil, tap } from 'rxjs';
import '@chancenwerk/client/design/components/cr-nospace-toggle-content.js';
import '../components/board-component-course-aggregated-detail-card.js';
import '../components/board-component-course-detail-card.js';
import '../components/board-component-drag-drop.js';
import '../components/board-component-user-card.js';
import '../components/board-component-user-count-stats.js';
import '../components/board-component-user-pool-card.js';
import '../components/board-component-user-pool.js';
import { dirtyData } from '../globalSubjects.js';
import { BoardRoutes } from '../routes.js';

const courseSelector: BaseQLSelectionSet<Course> = {
  _id: 1,
  _class: 1,
  kpfID: 1,
  location: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  leKoIDs: 1,
  osStudentIDs: 1,
  usStudentIDs: 1,
  schuKoIDs: 1,
  startDate: 1,
  plannedStartDate: 1,
  schuKos: { _id: 1, _class: 1, vorname: 1, username: 1, nachname: 1, email: 1 },
  leKos: { _id: 1, _class: 1, vorname: 1, username: 1, nachname: 1, email: 1 },
  osStudents: {
    _id: 1,
    _class: 1,
    vorname: 1,
    nachname: 1,
    username: 1,
    guestAccount: 1,
    inactive: 1,
    email: 1,
    schuelertyp: 1,
    schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
    kpfs: { school: { name: 1 } },
    kuendigung: { austrittsdatum: 1 },
  },
  usStudents: {
    _id: 1,
    _class: 1,
    vorname: 1,
    nachname: 1,
    username: 1,
    guestAccount: 1,
    inactive: 1,
    email: 1,
    schuelertyp: 1,
    schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
    kpfs: { school: { name: 1 } },
    kuendigung: { austrittsdatum: 1 },
  },
};

const studentSelector: BaseQLSelectionSet<Student> = {
  _id: 1,
  _class: 1,
  vorname: 1,
  username: 1,
  inactive: 1,
  nachname: 1,
  email: 1,
  guestAccount: 1,
  schuelertyp: 1,
  schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
  kuendigung: { austrittsdatum: 1 },
  kpfs: { school: { name: 1 } },
  lernfoerderungDuerchfuehrungsTagChoosen: DurchfuehrungsTag.allFields,
};

const lekoSelector: BaseQLSelectionSet<Mitarbeiter> = {
  _id: 1,
  _class: 1,
  username: 1,
  vorname: 1,
  nachname: 1,
  employeeID: 1,
};

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _id: 1,
  kpf: 1,
  schoolID: 1,
  school: { _class: 1, _id: 1, name: 1, kuerzel: 1 },
  schuKoIDs: 1,
  accessTokenForExternals: 1,
  accessTokenForStudents: 1,
  kuerzel: 1,
  schuKos: { _id: 1, _class: 1, vorname: 1, nachname: 1, email: 1, employeeID: 1 },
  leKos: { _id: 1, _class: 1 },
  produkt: {
    _class: 1,
    USLernFoerderung: 1,
    OSLernFoerderung: 1,
    uebungsLeiterProLernFoerderung: 1,
    kuerzel: 1,
    durchfuehrungsTage: DurchfuehrungsTag.allFields,
  },
};

/**
 * @customElement board-page-course-details-chancenschule
 */
@customElement('board-page-course-details-chancenschule')
export class BoardPageCourseDetails extends CrPage {
  override styles() {
    return [...super.styles(), { ':host': { height: '100%' } }];
  }

  constructor() {
    super();
    this.label = 'Chancenschule';
    this.backRoute = BoardRoutes.overview;
  }

  @property() public kpfID = new RXController(this, '');

  @state() private readonly _kpf = new RXController(
    this,
    this.kpfID.observable.pipe(switchMap(id => KPF.getByID<KPF>(id ?? '')(kpfSelector))),
  );

  @state() private readonly _coursesFromKPF = new RXController(
    this,
    this._kpf.observable.pipe(
      switchMap(kpf => Course.subscribeCoursesByKPFID([kpf?._id ?? ''], CourseChancenschule._class)(courseSelector)),
      tap(data => dirtyData.next(data)),
    ),
  );

  @state() private readonly _courseMap = new RXController(
    this,
    this._coursesFromKPF.observable.pipe(
      map(courses => {
        const courseMap = new Map<string, Course[]>();

        for (const course of courses ?? []) {
          const key = course.weekday ?? '';
          const entry = courseMap.get(key);
          if (!entry) {
            courseMap.set(key, [course]);
          } else {
            courseMap.set(key, [...entry, course]);
          }
        }
        return courseMap;
      }),
    ),
  );

  @state() private readonly _kpfStudentsUS = new RXController(
    this,
    this._kpf.observable.pipe(
      switchMap(kpf =>
        UserAPI.subscribeByFilter(
          0,
          0,
          new ChancenwerkUserFilter({
            userClass: [Student._class],
            aspects: [
              new BooleanAspect({ customID: 'active', value: true }),
              new KPFAspect({ value: kpf?._id ?? '' }),
              new StudentTypeAspect({ value: StudentType.US }),
            ],
          }),
        )(studentSelector),
      ),
    ),
  );

  @state() private readonly _kpfLeKos = new RXController(
    this,
    this._kpf.observable.pipe(
      switchMap(kpf =>
        UserAPI.subscribeByFilter(
          0,
          0,
          new ChancenwerkUserFilter({
            userClass: [VerwaltungsMitarbeiter._class, Mitarbeiter._class, ExternerMitarbeiter._class],
            aspects: [
              new ChancenRoleAspect({ value: Role.LEKO }),
              new BooleanAspect({ customID: 'active', value: true }),
              new KPFAspect({ value: kpf?._id ?? '' }),
            ],
          }),
        )(lekoSelector),
      ),
    ),
  );

  private readonly _scroll: Ref<ChromiumFlex> = createRef();

  override connectedCallback(): void {
    super.connectedCallback();

    this._kpf.observable
      .pipe(
        takeUntil(this.disconnected),
        tap(kpf => {
          this.label = `Chancenschule ${kpf?.kuerzel ?? ''}`;
        }),
      )
      .subscribe();
  }

  override header() {
    return html`
      <cr-flex space="md" horizontal crossaxis-center>
        <board-component-user-count-stats
          ?loading=${this._coursesFromKPF.isLoading || this._courseMap.isLoading}
          .elements=${{
            LeKos: this._kpfLeKos.value
              ? html`<cr-text>${this._kpfLeKos.value.length}</cr-text>`
              : html`<cr-loader ${css({ height: '12px', width: '12px' })}></cr-loader>`,
            Kinder: this._kpfStudentsUS.value?.length ?? 0,
          }}
        >
        </board-component-user-count-stats>
        <cr-button
          mode="action"
          @click=${async () => {
            await ChromiumDialog.waitFor(this._saveAll());
          }}
        >
          Speichern
        </cr-button>
      </cr-flex>
    `;
  }

  override content() {
    if (this._coursesFromKPF.isLoading || this._courseMap.isLoading)
      return html`
        <cr-flex center crossaxis-center>
          <cr-loader></cr-loader>
        </cr-flex>
      `;
    return html`
      <cr-flex space="sm" padding="xs" flex horizontal ${css({ minHeight: 0 })}>
        <cr-flex  ${ref(this._scroll)} space="md" ${css({
          flex: 4,
          overflowY: 'auto',
          paddingBottom: this.spacing.lg,
        })} >
          ${repeat(
            [...(this._courseMap.value?.keys() ?? [])].sort((a, b) => {
              const aWeekday = weekdayIndex(a as Weekday);
              const bWeekday = weekdayIndex(b as Weekday);
              return aWeekday - bWeekday;
            }),
            k => k,
            location => {
              const courses = this._courseMap.value?.get(location);
              return html`<board-component-course-aggregated-detail-card
                .dropzonesEnabled=${['LeKo', 'US']}
                .coursesToManage=${courses}
                .kpf=${this._kpf.value}
                @dropped=${() => this.requestUpdate()}
                hide-edit
              ></board-component-course-aggregated-detail-card>`;
            },
          )}
        </cr-flex>

        <board-component-user-pool-card>       
        <x-flex slot="overTitle" ${css({
          display: this._coursesFromKPF.value?.some(c => c.isDirty()) ? 'block' : 'none',
        })}>
          ${CrDataWarning(this._coursesFromKPF.value?.some(c => c.isDirty()))}
        </x-flex>

        <board-component-user-pool label="Kinder" .kpfID=${this.kpfID.value} .users=${
          this._kpfStudentsUS.value ?? []
        } .userType=${'US'}></board-component-user-pool>
        <board-component-user-pool label="LeKos"  .users=${
          this._kpfLeKos.value
        } .userType=${'LeKo'}></board-component-user-pool>
      </cr-flex>
    `;
  }

  private async _saveAll() {
    for (const c of this._coursesFromKPF.value ?? []) {
      await c.save();
    }
  }
}
